import React, { useState } from 'react';
import {
  Code,
  Brush,
  Layout,
  Palette,
  MousePointer2,
  House,
  User,
  Cog,
  MonitorSmartphone,
} from 'lucide-react';
import './style.css';
import { useTranslation } from 'react-i18next';

import FrontendSimulation from '../simulation/DevelopmentSimulation';
import ColorPalette from '../colorPalette/ColorPalette';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebookF,
  faGithub,
  faInstagram,
  faXTwitter,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';

import ButtonShowcase from '../ButtonShowcase/ButtonShowcase';
import ProductCardsDemo from '../productCardsDemo/ProductCardsDemo';
import SmartFormDemo from '../SmartFormDemo/SmartFormDemo';
import InteractiveServicesDemo from '../InteractiveServicesDemo/InteractiveServicesDemo';
import AnimatedNavigation from '../AnimatedNavigation/AnimatedNavigation';

import { SocialIconsB } from '../socialIcons/SocialIcons';
import { ResponsiveCard } from '../responsiveCard/ResponsiveCard'

const NavTabs = ({ t }) => {
  const [activeTab, setActiveTab] = useState('home');

  return (
    <div className="neo-tabs-container">
      <ul className="neo-nav-list">
        <li className="neo-nav-item">
          <button
            className={`neo-nav-link ${activeTab === 'home' ? 'active' : ''}`}
            onClick={() => setActiveTab('home')}
          >
            {t('tabs.home')}
          </button>
        </li>
        <li className="neo-nav-item">
          <button
            className={`neo-nav-link ${
              activeTab === 'profile' ? 'active' : ''
            }`}
            onClick={() => setActiveTab('profile')}
          >
            {t('tabs.profile')}
          </button>
        </li>
        <li className="neo-nav-item">
          <button
            className={`neo-nav-link ${
              activeTab === 'contact' ? 'active' : ''
            }`}
            onClick={() => setActiveTab('contact')}
          >
            {t('tabs.contact')}
          </button>
        </li>
      </ul>

      <div className="neo-tab-content">
        {activeTab === 'home' && (
          <div className="tab-pane active">{t('tabsContent.home')}</div>
        )}
        {activeTab === 'profile' && (
          <div className="tab-pane">{t('tabsContent.profile')}</div>
        )}
        {activeTab === 'contact' && (
          <div className="tab-pane">{t('tabsContent.contact')}</div>
        )}
      </div>
    </div>
  );
};

// Компонент для социальных иконок
const SocialIconsA = ({ t }) => {
  return (
    <ul className="socialA-list">
      <li className="socialA-item socialA-item--facebook">
        <span className="socialA-tooltip">{t('social.facebook')}</span>
        <span className="socialA-icon">
          <FontAwesomeIcon icon={faFacebookF} />
        </span>
      </li>
      <li className="socialA-item socialA-item--twitter">
        <span className="socialA-tooltip">{t('social.twitter')}</span>
        <span className="socialA-icon">
          <FontAwesomeIcon icon={faXTwitter} />
        </span>
      </li>
      <li className="socialA-item socialA-item--instagram">
        <span className="socialA-tooltip">{t('social.instagram')}</span>
        <span className="socialA-icon">
          <FontAwesomeIcon icon={faInstagram} />
        </span>
      </li>
      <li className="socialA-item socialA-item--github">
        <span className="socialA-tooltip">{t('social.github')}</span>
        <span className="socialA-icon">
          <FontAwesomeIcon icon={faGithub} />
        </span>
      </li>
      <li className="socialA-item socialA-item--youtube">
        <span className="socialA-tooltip">{t('social.youtube')}</span>
        <span className="socialA-icon">
          <FontAwesomeIcon icon={faYoutube} />
        </span>
      </li>
    </ul>
  );
};

const ButtonEffectsDemo = ({ t }) => {
  return (
    <div className="button-effects-demo">
      <button className="btn ripple-btn">
        <span>{t('buttonEffects.ripple')}</span>
      </button>
      <button className="btn morph-btn">
        <span>{t('buttonEffects.morph')}</span>
      </button>
      <button className="btn glow-btn">
        <span>{t('buttonEffects.glow')}</span>
      </button>
      <button className="btn squeeze-btn">
        <span>{t('buttonEffects.squeeze')}</span>
      </button>
    </div>
  );
};

const BentoGridPage = () => {
  const { t } = useTranslation('design');

  return (
    <section className="section">
      <div className="container">
        <header className="headerUI">
          <div className="header-content">
            <div className="tag__subtitle">{t('header.subtitle')}</div>
            <h1 className="title-2">
              {t('header.titleBefore')}{' '}
              <span className="accent">{t('header.titleAccent')}</span>
            </h1>
            <p className="main-description">{t('header.description')}</p>
          </div>
        </header>

        <div className="bento-grid-container">
          {/* 1: Планирование */}
          <div className="bento-item planning-card">
            <div className="bento-icon-wrapper">
              <Layout size={36} />
            </div>
            <h3 className="bento-title">{t('cards.planning.title')}</h3>
            <p className="bento-description">{t('cards.planning.desc')}</p>
          </div>

          {/* 2: Дизайн */}
          <div className="bento-item design-card">
            <div className="bento-icon-wrapper">
              <Brush size={36} />
            </div>
            <h3 className="bento-title">{t('cards.design.title')}</h3>
            <p className="bento-description">{t('cards.design.desc')}</p>
          </div>

          {/* 2.1: Психология цвета */}
          <div className="bento-item palette-card">
            <div className="bento-icon-wrapper">
              <Palette size={36} />
            </div>
            <h3 className="bento-title">{t('cards.palette.title')}</h3>
            <p className="bento-description">{t('cards.palette.desc')}</p>
            <ColorPalette />
          </div>

          {/* 4: Glassmorphism */}
          <div className="bento-item glass-card">
            <div className="glass-content">
              <h3 className="bento-title">{t('cards.glass.title')}</h3>
              <p className="bento-description">{t('cards.glass.desc')}</p>
              <button className="glass-button">{t('cards.glass.cta')}</button>
            </div>
          </div>

          {/* 5: Neomorphism */}
          <div className="bento-item neo-card">
            <h3 className="bento-title">{t('cards.neo.title')}</h3>
            <p className="bento-description">{t('cards.neo.desc')}</p>
            <div className="neo-example-content">
              <NavTabs t={t} />
            </div>
            <div className="neo-item__wrapper">
              <div className="neo-item">
                <MousePointer2 size={36} />
              </div>
              <div className="neo-item">
                <House size={36} />
              </div>
              <div className="neo-item">
                <User size={36} />
              </div>
              <div className="neo-item">
                <Cog size={36} />
              </div>
            </div>
            <AnimatedNavigation />
          </div>

          {/* 6: Frontend */}
          <div className="bento-item frontend-card">
            <div className="bento-icon-wrapper">
              <Code size={36} />
            </div>
            <h3 className="bento-title">{t('cards.frontend.title')}</h3>
            <p className="bento-description">{t('cards.frontend.desc')}</p>
            <FrontendSimulation />
          </div>

          {/* 7: Виды карточек */}
          <div className="bento-item card-types-card">
            <h3 className="bento-title">{t('cards.productCards.title')}</h3>
            <p className="bento-description">{t('cards.productCards.desc')}</p>
            <ProductCardsDemo />
          </div>

          {/* 8: Кнопки */}
          <div className="bento-item buttons-card">
            <h3 className="bento-title">{t('cards.buttonsCard.title')}</h3>
            <p className="bento-description">{t('cards.buttonsCard.desc')}</p>

            <div className="button-examples">
              <button className="btnBento filled">
                {t('buttons.primary')}
              </button>
              <button className="btnBento outline">
                {t('buttons.secondary')}
              </button>
              <button className="btnBento text">{t('buttons.tertiary')}</button>
            </div>

            <div className="button-divider">
              <span>{t('buttons.dividerHover')}</span>
            </div>

            <ButtonEffectsDemo t={t} />

            <div className="button-divider">
              <span>{t('buttons.dividerStates')}</span>
            </div>
            <ButtonShowcase />
          </div>

          {/* 9: Каталог услуг */}
          <div className="bento-item services-card">
            <h3 className="bento-title">{t('cards.services.title')}</h3>
            <p className="bento-description">{t('cards.services.desc')}</p>
            <InteractiveServicesDemo />
          </div>

          {/* 10: Соцсети */}
          <div className="bento-item social-card">
            <div>
              <h3 className="bento-title">{t('cards.socialCard.title')}</h3>
              <p className="bento-description">{t('cards.socialCard.desc')}</p>
            </div>

            <div className="social-demoA">
              <SocialIconsA t={t} />
            </div>

            <div className="social-demoB">
              <SocialIconsB />
            </div>
          </div>

          {/* 11: Формы */}
          <div className="bento-item form-card">
            <h3 className="bento-title">{t('cards.forms.title')}</h3>
            <p className="bento-description">{t('cards.forms.desc')}</p>
            <div className="social-example-content">
              <SmartFormDemo />
            </div>
          </div>

          {/* 12: Responsive */}
          <div className="bento-item respo-card">
            <div className="bento-icon-wrapper">
              <MonitorSmartphone size={36} />
            </div>
            <h3 className="bento-title"> {t('cards.responsive.title')}</h3>
            <p className="bento-description">
              {t('cards.responsive.desc')}
            </p>
            <ResponsiveCard />
          </div>
        </div>
      </div>
    </section>
  );
};

export default BentoGridPage;